import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ControlePatrimonioArquivoLista } from "../../../../domain/models/controle_patrimonio_arquivo_lista";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal";
import { MaquinaEquipArquivoForm, MaquinaEquipArquivoSchema } from "../../../schemas/ControleDePatrimonioArquivo/ControleDePatrimonioArquivo";

import DefaultFormInput from "../../../../../../common/components/default_form_input";
import { DefaultFormFileInput } from "../../../../../../common/components/default_form_file_input";

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  selectedControlePatrimonioArquivo: ControlePatrimonioArquivoLista | null;
  onClose: () => void;
  onSubmit: (form: MaquinaEquipArquivoForm) => void;
  onDownloadFile: (idMaquinaEquipArquivo: number, imageName: string) => void;
};

export const ControlePatrimonioArquivoModal = ({
  open,
  saving,
  loading,
  selectedControlePatrimonioArquivo,
  onClose,
  onSubmit,
  onDownloadFile,
}: Props) => {
  const hookForm = useForm<MaquinaEquipArquivoForm>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    resolver: zodResolver(MaquinaEquipArquivoSchema),
  });

  const [limparArquivo] = hookForm.watch(['limparArquivo']);

  const filename = useMemo(() => {
    return selectedControlePatrimonioArquivo !== null && selectedControlePatrimonioArquivo.nome !== null
      ? selectedControlePatrimonioArquivo.nome
      : undefined;
  }, [
    selectedControlePatrimonioArquivo,
  ]);

  useEffect(() => {
    if (open) {
      if (selectedControlePatrimonioArquivo) {
        hookForm.setValue('descricao', selectedControlePatrimonioArquivo.descricao ?? "");
        hookForm.setValue('arquivo', null);
        hookForm.setValue('limparArquivo', false);
      } else {
        hookForm.reset();
      }
    }
  }, [
    open,
    selectedControlePatrimonioArquivo,
    hookForm,
  ]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title="Controle de Patrimônio - Arquivo"
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <div className="w-full flex flex-col gap-2 my-2">
        <DefaultFormInput
          name='descricao'
          label='Nome do Documento'
          required={true}
          reactHookForm={hookForm}
        />

        <DefaultFormFileInput
          name='arquivo'
          saving={saving}
          loading={loading}
          filename={limparArquivo ? undefined : filename}
          maxFilenameLength={80}
          reactHookForm={hookForm}
          onDownload={() => {
            if (selectedControlePatrimonioArquivo) {
              onDownloadFile(selectedControlePatrimonioArquivo.idMaquinaEquipArquivo, String(selectedControlePatrimonioArquivo.nome));
            }
          }}
          onChangeFile={() => hookForm.setValue('limparArquivo', false)}
          onLimparArquivo={() => {
            if (window.confirm("Tem certeza que deseja limpar o arquivo?")) {
              hookForm.setValue('arquivo', null);
              hookForm.setValue('limparArquivo', true);
            }
          }}
        />
      </div>
    </BaseFormControlledModal>
  );
}
